import React, { useEffect, useState } from "react";
import { LinearProgress } from "@mui/material";
import { UserWithTopics, UserRole } from "../../context/ConfigContext/types";
import { useAdminPanelContext } from "../../context/AdminPanelContext";
import UserForm, { UserFormProps } from "./UserForm";

import { updateClientUser } from "../../services/patch";
import { createClientUser } from "../../services/post";
import { deleteClientUser } from "../../services/delete";
import GenericPanelToggle, { PanelProps, PanelType } from "../GenericPanelToggle";
import { getAllUsersWithTopics } from "../../services/fetch";

const UsersPanel = () => {
  const { topics } = useAdminPanelContext();
  const [selectedUser, setSelectedUser] = useState<UserWithTopics>(null);
  const [users, setUsers] = useState<UserWithTopics[]>();
  const [lastUnsavedId, setLastUnsavedId] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getAllUsersWithTopics();
        setUsers(data.result);
      } catch (error) {
        console.error("Failed to fetch data for all users:", error);
      }
    };
    fetchData();
  }, []);

  const updateUsersAndSelectedUser = (updatedUser, unsavedId?) => {
    const idReference = unsavedId || updatedUser.id;
    setUsers((prevUsers) => prevUsers.map((user) => (user.id === idReference ? updatedUser : user)));
    setSelectedUser(updatedUser);
  };

  const handleSaveUser = async (updatedUser) => {
    setIsLoading(true);
    if (updatedUser.id > 0) {
      const response = await updateClientUser(updatedUser.id, updatedUser);
      const savedUser = response.result;
      updateUsersAndSelectedUser(savedUser);
    } else {
      const response = await createClientUser(updatedUser);
      const savedUser = response.result;
      updateUsersAndSelectedUser(savedUser, updatedUser.id);
    }
    setIsLoading(false);
  };

  const handleDeleteUser = async (id: number) => {
    setIsLoading(true);
    try {
      if (id > 0) await deleteClientUser(id);
      setUsers((prevUsers) => prevUsers.filter((user) => user.id !== id));
      if (selectedUser?.id === id) {
        setSelectedUser(null);
      }
    } catch (error) {
      console.error("Failed to delete user:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddNewUser = () => {
    const unsavedUser: UserWithTopics = {
      id: lastUnsavedId,
      clientId: users?.[0]?.clientId,
      lastName: "",
      firstName: "",
      email: "",
      adObjectId: "",
      role: UserRole.READER,
      topics: [],
    };
    setLastUnsavedId(lastUnsavedId - 1);
    setUsers((prevUsers) => [unsavedUser, ...prevUsers]);
    setSelectedUser(unsavedUser);
  };

  return (
    <>
      {!users?.length || isLoading ? (
        <LinearProgress />
      ) : (
        <GenericPanelToggle
          items={users}
          panelType={PanelType.CLIENT_USER}
          handleAddNew={handleAddNewUser}
          RenderForm={(props: PanelProps) => {
            return (
              <UserForm
                {...(props as UserFormProps)}
                topics={topics}
                onUserUpdate={handleSaveUser}
                onUserDelete={handleDeleteUser}
              />
            );
          }}
        />
      )}
    </>
  );
};

export default UsersPanel;
